import AppTableControl from '@/components/AppTableControl';
import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import { columnSorter, formatDate, formatNumber } from '@/utils/formatters';
import React, { useMemo, useState } from 'react';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getLoginStats } from '@/services/revenue.service';
import { Space, Table } from 'antd';
import dayjs from 'dayjs';
import { isArray } from 'lodash';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

const rowKey = 'key';

const START_DATE = dayjs().startOf('week').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function LoginStats() {
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);
  const [date, setDate] = useState([START_DATE, END_DATE]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getLoginStats({
        startDate: date?.[0],
        endDate: date?.[1],
      }),
    ['login-stats', date]
  );

  const columns = [
    {
      title: 'Login Time',
      dataIndex: 'login_time',
      key: 'login_time',
      width: isMobile ? 100 : 200,
      sorter: columnSorter('login_time'),
      // render: (text) => formatDate(text, 'DD/MM/YYYY'),
      fixed: 'left',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      children: [
        {
          title: 'WEB/WAP',
          key: 'web',
          render: (record) => {
            const value = record?.total.web;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
        {
          title: 'App',
          key: 'app',
          render: (record) => {
            const value = record?.total.app;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
        {
          title: 'OTP',
          key: 'otp',
          render: (record) => {
            const value = record?.total.otp;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
      ],
    },
    {
      title: 'Success',
      key: 'success',
      children: [
        {
          title: 'WEB/WAP',
          key: 'web',
          render: (record) => {
            const value = record?.success.web;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
        {
          title: 'App',
          key: 'app',
          render: (record) => {
            const value = record?.success.app;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
        {
          title: 'OTP',
          key: 'otp',
          render: (record) => {
            const value = record?.success.otp;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
      ],
    },
    {
      title: 'Fail',
      key: 'fail',
      children: [
        {
          title: 'WEB/WAP',
          key: 'web',
          render: (record) => {
            const value = record?.fail.web;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
        {
          title: 'App',
          key: 'app',
          render: (record) => {
            const value = record?.fail.app;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
        {
          title: 'OTP',
          key: 'otp',
          render: (record) => {
            const value = record?.fail.otp;
            return value > 0 ? formatNumber(value) : 0;
          },
        },
      ],
    },
  ];

  const formatData = useMemo(() => {
    if (!data || !isArray(data) || data.length === 0 || !date[0] || !date[1]) return [];

    const start = dayjs(date[0]);
    const end = dayjs(date[1]);

    const dailyData = new Map();
    let currentDate = start;
    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      const dateStr = currentDate.format('DD/MM/YYYY');
      const dayData = {
        key: dateStr,
        login_time: dateStr,
        total: {
          web: 0,
          app: 0,
          otp: 0,
        },
        success: {
          web: 0,
          app: 0,
          otp: 0,
        },
        fail: {
          web: 0,
          app: 0,
          otp: 0,
        },
      };

      dailyData.set(dateStr, dayData);
      currentDate = currentDate.add(1, 'day');
    }

    data.forEach((item) => {
      const itemDate = dayjs(item.report_date).format('DD/MM/YYYY');
      if (dailyData.has(itemDate)) {
        const result = dailyData.get(itemDate);
        if (item.channel === 'WEB') {
          result.total.web += item.total_success + item.total_fail;
          result.success.web += item.total_success;
          result.fail.web += item.total_fail;
        } else if (item.channel === 'APP') {
          result.total.app += item.total_success + item.total_fail;
          result.success.app += item.total_success;
          result.fail.app += item.total_fail;
        } else if (item.channel === 'OTP') {
          result.total.otp += item.total_success + item.total_fail;
          result.success.otp += item.total_success;
          result.fail.otp += item.total_fail;
        }
      }
    });

    return Array.from(dailyData.values()) ? Array.from(dailyData.values()).reverse() : [];
  }, [data, date]);

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Login Statistics" />
      <AppTableControl hasSelect={false} date={date} setDate={setDate} noStyle />

      <AppTable
        className="custom-table"
        columns={columns}
        dataSource={formatData || []}
        rowKey={rowKey}
        loading={isLoading || isRefetching}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        hasPagination={false}
        onRefresh={refetch}
        scroll={{ x: '1400px', y: '450px' }}
        summary={(pageData) => {
          // Calculate totals for each cell
          const summaryColumns = [
            { key: 'total', label: 'Total' },
            { key: 'success', label: 'Success' },
            { key: 'fail', label: 'Fail' },
          ];

          const subKeys = ['web', 'app', 'otp'];
          const summaryData = summaryColumns.flatMap((col) =>
            subKeys.map((sub) => ({
              key: `${col.key}_${sub}`,
              value: pageData.reduce((sum, row) => sum + (row[col.key]?.[sub] ?? 0), 0),
            }))
          );

          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} align="center" className="font-bold">
                  Total
                </Table.Summary.Cell>
                {summaryData.map((cell, idx) => (
                  <Table.Summary.Cell
                    key={cell.key}
                    index={idx + 1}
                    align="center"
                    className="font-bold"
                  >
                    {cell.value > 0 ? formatNumber(cell.value) : 0}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </Space>
  );
}

export default LoginStats;
